import React, { FC, useState, useRef, useMemo } from 'react'
import ModalBox from 'components/Common/ModalBox'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { CalendarClock, CircleAlert } from 'lucide-react'
import colors from 'colors'
import { CancelConfirmationModal } from './components/Modals/CancelConfirmationModal'
import { TimezoneSelector } from './components/TimezoneSelector/TimezoneSelector'
import { SuggestedTimes } from './components/SuggestedTimes/SuggestedTimes'
import { GuestManagement } from './components/GuestManagement/GuestManagement'
import { MeetingDetailsForm } from './components/MeetingDetailsForm/MeetingDetailsForm'
import { DateTimePicker } from './components/DateTimePicker/DateTimePicker'
import FindTimeCalendar from './components/FindTimeCalendar/FindTimeCalendar'
import {
  useAttendeeCalendar,
  useCalendarData,
  useMeetingHandlers,
  useSuggestedTimes,
  useAssigneeOptions,
  useModalEffects,
  useTimeComputations,
} from './hooks'
import type { ICreateMeetingPopupProps } from './types'
import {
  formatTimeString,
  addMinutesToTime,
  getTimezoneDisplay,
  getValidStartTimes,
  getValidEndTimes,
} from './helpers'

const CreateMeetingPopup: FC<ICreateMeetingPopupProps> = ({
  isOpen,
  onClose,
  defaultAttendee,
  defaultMeetingTitle,
  onSuccess,
}) => {
  // Memoize device timezone to avoid repeated calls
  const deviceTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const [meetingTitle, setMeetingTitle] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [showCalendar, setShowCalendar] = useState(false)
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date())
  const [startTime, setStartTime] = useState<string>('')
  const [endTime, setEndTime] = useState<string>('')
  const [showStartTimePicker, setShowStartTimePicker] = useState(false)
  const [showEndTimePicker, setShowEndTimePicker] = useState(false)
  const [showFindTimeCalendar, setShowFindTimeCalendar] = useState(false)
  const [findTimeView, setFindTimeView] = useState<'Day' | '3 Days' | 'Week'>('3 Days')
  const [findTimeCurrentDate, setFindTimeCurrentDate] = useState(new Date())
  const [guestSearchQuery, setGuestSearchQuery] = useState('')
  const [showGuestDropdown, setShowGuestDropdown] = useState(false)
  const [selectedGuests, setSelectedGuests] = useState<IAssigneeOptions[]>([])
  const [emailError, setEmailError] = useState('')
  const [validationErrors, setValidationErrors] = useState({
    title: '',
    date: '',
    time: '',
    guests: '',
    meetingLink: '',
  })
  const [selectedTimezone, setSelectedTimezone] = useState<string>(deviceTimezone)
  const [highlightedGuestIndex, setHighlightedGuestIndex] = useState(-1)
  const [selectedAttendeeFilter, setSelectedAttendeeFilter] = useState<string[]>(['all']) // array of 'all' or userIds for multi-select
  const [meetingLink, setMeetingLink] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [meetingDescription, setMeetingDescription] = useState('')
  const [meetingLocation, setMeetingLocation] = useState('')
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [temporarySlot, setTemporarySlot] = useState<{
    date: Date
    startHour: number
    startMinute: number
  } | null>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const startTimeRef = useRef<HTMLDivElement>(null)
  const endTimeRef = useRef<HTMLDivElement>(null)
  const timeGridRef = useRef<HTMLDivElement>(null)
  const guestItemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const isCalendarClickRef = useRef(false) // Track if time change is from calendar click

  // Get current user info
  const currentUser = useSelector((state: RootState) => state.user.user)
  const tenantId = useSelector((state: RootState) => state.user.tenantId)
  const isAdvisor =
    currentUser?.roles?.includes('BSO_ADVISOR') || currentUser?.roles?.includes('BSO_ADMIN')

  // Get assignee options based on user role
  const assigneeOptions = useAssigneeOptions({ isAdvisor, tenantId })

  // Use custom hook for calendar data fetching (computes attendees internally)
  const { userAvailabilityMap, pendingGuestRequests, fetchedGuestsRef } = useCalendarData({
    showFindTimeCalendar,
    selectedGuests,
    findTimeCurrentDate,
  })

  // Use custom hook for attendee calendar logic with fetched data
  const { allAttendees, internalAttendees, attendeeColors, getAttendeeEvents } =
    useAttendeeCalendar({
      selectedGuests,
      userAvailabilityMap,
    })

  // Use custom hook for suggested times calculation
  const { suggestedTimes, loadingSuggestions } = useSuggestedTimes({
    selectedGuests,
    internalAttendees,
    userAvailabilityMap,
    getAttendeeEvents,
  })

  // Filter users based on search query and exclude already selected guests and the organizer
  const filteredUsers = useMemo(() => {
    if (!assigneeOptions) {
      return []
    }

    const searchQuery = guestSearchQuery.toLowerCase()
    const organizerEmail = currentUser?.email?.toLowerCase()
    return assigneeOptions.filter((user) => {
      // Search by name OR email
      const matchesName = user.label.toLowerCase().includes(searchQuery)
      const matchesEmail =
        user.email?.toLowerCase().includes(searchQuery) ||
        user.user?.email?.toLowerCase().includes(searchQuery)
      const matchesSearch = matchesName || matchesEmail
      const notAlreadySelected = !selectedGuests.some(
        (guest) =>
          guest.value === user.value || guest.email?.toLowerCase() === user.email?.toLowerCase()
      )
      // Exclude organizer by both ID and email
      const notOrganizer =
        user.value !== currentUser.id &&
        user.email?.toLowerCase() !== organizerEmail &&
        user.user?.email?.toLowerCase() !== organizerEmail

      return matchesSearch && notAlreadySelected && notOrganizer
    })
  }, [assigneeOptions, guestSearchQuery, selectedGuests, currentUser.id, currentUser?.email])

  // Use custom hook for all event handlers
  const {
    handleDateSelect,
    handleStartTimeSelect,
    handleEndTimeSelect,
    handleGuestSelect,
    handleRemoveGuest,
    handleAddGuestByEmail,
    handleCopyMeetingLink,
    handleGuestInputKeyPress,
    handleSuggestedTimeClick,
    handleCancelClick,
    handleConfirmCancel,
    handleCancelConfirmation,
    handleCreateMeeting,
  } = useMeetingHandlers({
    selectedGuests,
    setSelectedGuests,
    guestSearchQuery,
    setGuestSearchQuery,
    setShowGuestDropdown,
    setEmailError,
    setHighlightedGuestIndex,
    fetchedGuestsRef,
    pendingGuestRequests,
    setSelectedDate,
    setShowCalendar,
    setShowStartTimePicker,
    setShowEndTimePicker,
    setStartTime,
    setEndTime,
    endTime,
    setTemporarySlot,
    isCalendarClickRef,
    meetingLink,
    setLinkCopied,
    filteredUsers,
    highlightedGuestIndex,
    meetingTitle,
    selectedDate,
    startTime,
    selectedTimezone,
    currentUser,
    tenantId,
    isAdvisor,
    meetingDescription,
    meetingLocation,
    onClose,
    onSuccess,
    setShowCancelConfirmation,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
    assigneeOptions,
    validationErrors,
    setValidationErrors,
  })

  // Use custom hook for modal side effects (auto-scroll, reset on close, etc.)
  useModalEffects({
    isOpen,
    showFindTimeCalendar,
    selectedTimezone,
    highlightedGuestIndex,
    startTime,
    deviceTimezone,
    timeGridRef,
    guestItemRefs,
    temporarySlot,
    isCalendarClickRef,
    selectedDate,
    fetchedGuestsRef,
    pendingGuestRequests,
    defaultAttendee,
    defaultMeetingTitle,
    setShowCalendar,
    setShowStartTimePicker,
    setShowEndTimePicker,
    setShowFindTimeCalendar,
    setShowGuestDropdown,
    setGuestSearchQuery,
    setSelectedGuests,
    setEmailError,
    setSelectedDate,
    setStartTime,
    setEndTime,
    setSelectedTimezone,
    setCurrentCalendarDate,
    setFindTimeCurrentDate,
    setFindTimeView,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
    setLinkCopied,
    setTemporarySlot,
    setSelectedAttendeeFilter,
    setValidationErrors,
  })

  // Use custom hook for time-related computations
  const { displayedStartTime, displayedEndTime } = useTimeComputations({
    selectedDate,
    startTime,
    endTime,
    selectedTimezone,
    deviceTimezone,
  })

  if (!isOpen) {
    return null
  }

  return (
    <>
      <ModalBox
        title="Create Meeting"
        subtitle="Schedule a meeting with anyone in GoTackle"
        onClose={onClose}
        height="h-[90vh]"
        width="w-[80vw] max-w-[785px]"
        padding="px-6 pt-6"
        bgColor="bg-black/50 backdrop-blur-sm"
        childClass="overflow-hidden rounded-b-xl flex flex-col"
        isBorderWidthFull={false}
      >
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-3">
          {/* Meeting Title */}
          <div className="mb-5">
            <label className="block text-sm font-semibold text-primary-text mb-2">
              Meeting Title*
            </label>
            <div
              className={`flex items-center border rounded-lg px-3 py-2 shadow ${
                validationErrors.title ? 'border-alert' : 'border-primary-outline'
              }`}
              style={validationErrors.title ? { borderColor: colors.alert.DEFAULT } : {}}
            >
              <input
                type="text"
                value={meetingTitle}
                onChange={(e) => {
                  setMeetingTitle(e.target.value)
                  if (validationErrors.title) {
                    setValidationErrors({ ...validationErrors, title: '' })
                  }
                }}
                placeholder="Enter your meeting title"
                className="flex-1 text-sm outline-none bg-transparent"
              />
            </div>
            {validationErrors.title && (
              <p
                className="text-xs mt-1.5 flex items-center gap-1 font-medium"
                style={{ color: colors.alert.DEFAULT }}
              >
                <CircleAlert className="w-3.5 h-3.5 flex-shrink-0" color={colors.alert.DEFAULT} />
                {validationErrors.title}
              </p>
            )}
          </div>

          {/* Date & Time */}
          <DateTimePicker
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            showCalendar={showCalendar}
            onShowCalendarChange={setShowCalendar}
            currentCalendarDate={currentCalendarDate}
            onCurrentCalendarDateChange={setCurrentCalendarDate}
            startTime={startTime}
            onStartTimeSelect={handleStartTimeSelect}
            showStartTimePicker={showStartTimePicker}
            onShowStartTimePickerChange={setShowStartTimePicker}
            displayedStartTime={displayedStartTime}
            validStartTimes={getValidStartTimes(selectedDate, selectedTimezone, deviceTimezone)}
            endTime={endTime}
            onEndTimeSelect={handleEndTimeSelect}
            showEndTimePicker={showEndTimePicker}
            onShowEndTimePickerChange={setShowEndTimePicker}
            displayedEndTime={displayedEndTime}
            validEndTimes={getValidEndTimes(startTime)}
            calendarRef={calendarRef}
            startTimeRef={startTimeRef}
            endTimeRef={endTimeRef}
            dateError={validationErrors.date}
            timeError={validationErrors.time}
          />

          {/* Timezone */}
          <div className="flex items-center justify-between mb-6 gap-4">
            <TimezoneSelector
              selectedTimezone={selectedTimezone}
              onTimezoneChange={setSelectedTimezone}
              deviceTimezone={getTimezoneDisplay(selectedTimezone)}
            />

            <button
              className="text-sm border border-primary-outline text-primary hover:bg-primary-brandLight flex items-center gap-1 px-3 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0 shadow"
              onClick={() => {
                setShowFindTimeCalendar(!showFindTimeCalendar)
              }}
            >
              <CalendarClock className="w-4 h-4" color={colors.primary.DEFAULT} />
              {showFindTimeCalendar ? 'Suggested Times' : 'Find Time'}
            </button>
          </div>

          {/* Find Time Calendar - Shows when Find Time is clicked */}
          <FindTimeCalendar
            show={showFindTimeCalendar}
            view={findTimeView}
            currentDate={findTimeCurrentDate}
            selectedAttendeeFilter={selectedAttendeeFilter}
            allAttendees={allAttendees}
            getAttendeeEvents={getAttendeeEvents}
            attendeeColors={attendeeColors}
            temporarySlot={temporarySlot}
            timezone={selectedTimezone}
            timeGridRef={timeGridRef}
            isCalendarClickRef={isCalendarClickRef}
            onViewChange={setFindTimeView}
            onDateChange={setFindTimeCurrentDate}
            onMonthChange={(monthIndex) => {
              const newDate = new Date(findTimeCurrentDate)
              newDate.setMonth(monthIndex)
              setFindTimeCurrentDate(newDate)
            }}
            onAttendeeFilterChange={setSelectedAttendeeFilter}
            onTimeSlotClick={(date, hour, startMinute) => {
              // Set flag to indicate this is a calendar click (prevents clearing temporary slot)
              isCalendarClickRef.current = true

              // Set the selected date
              setSelectedDate(new Date(date))

              // Set temporary slot for visual feedback FIRST
              setTemporarySlot({
                date: new Date(date),
                startHour: hour,
                startMinute: startMinute,
              })

              // Format and set start time
              const startTimeString = formatTimeString(hour, startMinute)
              setStartTime(startTimeString)

              // Calculate end time (15 minutes later)
              const endTimeString = addMinutesToTime(startTimeString, 15)
              setEndTime(endTimeString)
            }}
          />

          {/* Suggested Times - Show when guests are added and Find Time calendar is not showing */}
          {(loadingSuggestions || suggestedTimes.length > 0) &&
            !showFindTimeCalendar &&
            selectedGuests.length > 0 && (
              <SuggestedTimes
                suggestedTimes={suggestedTimes}
                isLoading={loadingSuggestions}
                onTimeSelect={handleSuggestedTimeClick}
              />
            )}

          {/* Guest Management */}
          <GuestManagement
            guestSearchQuery={guestSearchQuery}
            onSearchQueryChange={(query) => {
              setGuestSearchQuery(query)
              // Clear guest validation error when user starts typing
              if (validationErrors.guests) {
                setValidationErrors({ ...validationErrors, guests: '' })
              }
            }}
            showDropdown={showGuestDropdown}
            onShowDropdownChange={setShowGuestDropdown}
            emailError={emailError}
            onEmailErrorChange={setEmailError}
            guestError={validationErrors.guests}
            filteredUsers={filteredUsers}
            highlightedIndex={highlightedGuestIndex}
            onHighlightedIndexChange={setHighlightedGuestIndex}
            selectedGuests={selectedGuests}
            onGuestSelect={handleGuestSelect}
            onGuestRemove={handleRemoveGuest}
            onAddGuestByEmail={handleAddGuestByEmail}
            onKeyPress={handleGuestInputKeyPress}
            guestItemRefs={guestItemRefs}
            currentUserId={currentUser.id}
            allAttendees={allAttendees}
            assigneeOptions={assigneeOptions}
          />

          {/* Meeting Details Form */}
          <MeetingDetailsForm
            location={meetingLocation}
            onLocationChange={setMeetingLocation}
            meetingLink={meetingLink}
            onMeetingLinkChange={(link) => {
              setMeetingLink(link)
              if (validationErrors.meetingLink) {
                setValidationErrors({ ...validationErrors, meetingLink: '' })
              }
            }}
            linkCopied={linkCopied}
            onCopyLink={handleCopyMeetingLink}
            linkError={validationErrors.meetingLink}
            description={meetingDescription}
            onDescriptionChange={setMeetingDescription}
          />
        </div>

        {/* Buttons - Fixed at bottom */}
        <div className="px-6 py-4 border-t border-primary-outline flex-shrink-0">
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelClick}
              className="px-4 py-2 border border-primary-outline text-primary-text hover:text-primary-hover text-sm rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateMeeting}
              className="px-5 py-2 text-sm bg-primary-appBrand text-white rounded-lg hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary"
            >
              Create Meeting
            </button>
          </div>
        </div>
      </ModalBox>

      {/* Cancel Confirmation Dialog */}
      <CancelConfirmationModal
        isOpen={showCancelConfirmation}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelConfirmation}
      />
    </>
  )
}

export default CreateMeetingPopup
